/**
 * PackHeroStage.module.scss
 *
 * Visual styles for the workbench-as-hero component. Phase 2 Handoff §08 responsive.
 *
 * Technique sources:
 *   - Shimmer: reuses TakeStrip shimmerSlide keyframe pattern (same bands, 2.2s).
 *   - Tokens: all from narcis design system (colors.scss, fonts via var()).
 */

@import '@styles/fonts.scss';
@import '@styles/mixins.scss';
@import '@styles/colors.scss';

// ── Stage container ───────────────────────────────────────────────────────────
// The right column of the hero grid: flex column, portrait-first.

.stage {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    height: 100%;
    min-height: 0;
    // Portrait cap: max-width that keeps a 3:4 image within min(660px, 80vh) of height (660 × 3/4
    // = 495px). Single source — inherits to .bigArea (descendant) and drives the centering.
    --hero-portrait-cap: calc(min(660px, 80vh) * 3 / 4);

    // Cap + center the whole stack at every width — otherwise the capped .bigArea hugs one edge
    // (the copy edge on desktop, the left edge on tablet). Phones narrower than the cap fill
    // naturally (width:100% < cap, so margin-inline:auto is a no-op); tablet and desktop center.
    max-width: var(--hero-portrait-cap);
    margin-inline: auto;

    // The shared stage pieces (`@core/packs/stage/`) read these only.
    --stage-accent: var(--yolk);
    --stage-ground: var(--surface);
    --stage-text: var(--text);
    --stage-muted: var(--text-muted);
    --stage-line: var(--border);
    --stage-radius: 16px;
}

// The coda fills the card it replaces: the card keeps its budgeted shape at every width, so the
// frame's own minimum ratio yields to the card's box here.
.codaFill {
    position: absolute;
    inset: 0;
    z-index: 6;
    display: flex;

    > * {
        height: 100%;
        aspect-ratio: auto !important;
    }
}

// ── Big portrait area ─────────────────────────────────────────────────────────
// aspect-ratio: 3/4 enforces portrait orientation from the column width.
// The stage column (~528px at desktop) drives the height (~704px), matching the
// handoff spec §08 right-column size. overflow:hidden + position:relative for
// all absolutely positioned overlays (chip, after-label, pager, coda).

.bigArea {
    position: relative;
    // aspect-ratio: 3/4 + max-height alone breaks the ratio (browser caps height, width stays).
    // Solution: cap max-width to the height equivalent so both dimensions stay in ratio. The cap
    // (--hero-portrait-cap, ~495×660 at 660px height) is defined once on .stage and inherited here.
    width: 100%;
    max-width: min(100%, var(--hero-portrait-cap));
    aspect-ratio: 3 / 4;
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid var(--border);
    background: var(--surface);
}

// §15 stage vertical budget (canvas 06 revision, 2026-07-24) — desktop-only fix for the "strip
// falls past the fold on a 14in laptop" bug. First attempt capped the whole .stage column
// (max-height + flex:1/min-height:0 on this aspect-ratio box) and applied it at every width —
// that collapses an aspect-ratio box built on a percentage-derived height inside an unresolved
// flex basis, and broke tablet/mobile, where the stage is the page hero (copy sits below it) and
// the page is meant to scroll. Corrected to cap ONE element, desktop-only: this box keeps its
// aspect-ratio (4/4.1 here, not 3/4 — the budgeted state trades strict portrait ratio for fitting
// the fold) and gets width:auto instead of 100%, so the ratio derives width FROM the
// (viewport-capped) height — the card narrows rather than overflowing. Nothing else resizes;
// the strip stays under the card unchanged (§07/§15's own verdict: an overlaid strip eats the
// picture, the one thing the user came to look at).
@media (min-width: 1024px) and (min-height: 640px) {
    .bigArea {
        aspect-ratio: 4 / 4.1;
        width: auto;
        margin-inline: auto;
        // `height`, not `max-height`: every child here (.bigImage, the overlays) is
        // position:absolute, so .bigArea has no in-flow content to derive an intrinsic height
        // from — max-height alone clamps a height that never gets computed in the first place,
        // collapsing the box to 0 (confirmed live: the image vanished on first deploy of this
        // rule). A definite `height` gives aspect-ratio something to derive width from; the
        // max(380px, …) floor already bounds it from below, so `height` alone carries the same
        // budget the operator specified without a separate max-height needed on top.
        //
        // 56px header + the fixed rows that follow (strip ~60 + locked note ~18 + gaps ≈ 150) =
        // 206px, floored at 380px so a very short (but still ≥640) window scrolls the page rather
        // than crushing the image. svh, never vh/100vh — mobile chrome retraction doesn't apply
        // here (desktop-only), but the unit stays honest to the formula's own source.
        height: max(380px, calc(100svh - 206px));
    }
}

// ── Big area image ────────────────────────────────────────────────────────────

.bigImage {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 18%;
    display: block;
    z-index: 1;
}

// ── Trainbar (copy spec §3 state C, canvas 02-C/06 §03) ───────────────────────
// A sibling of .bigArea, not nested in it.

.trainbar {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-md, 10px);
    padding: 14px 16px;
}

.trainbarRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 9px;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.trainbarStatus {
    color: var(--text);
    font-weight: 400;
    display: flex;
    align-items: center;
    gap: 8px;
}

.trainbarDot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--yolk);
    flex-shrink: 0;
    animation: wbPulse 1.4s ease-in-out infinite;
}

.trainbarMeta {
    color: var(--text-dim);
}

.trainbarBar {
    height: 3px;
    background: var(--surface-hover);
    border-radius: var(--radius-full, 9999px);
    overflow: hidden;

    i {
        display: block;
        height: 100%;
        background: var(--yolk);
        border-radius: var(--radius-full, 9999px);
        transition: width 0.6s ease;
    }
}

.trainbarSetRow {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 11px;
}

.trainbarSetThumb {
    position: relative;
    width: 32px;
    aspect-ratio: 3 / 4;
    border-radius: 5px;
    overflow: hidden;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.trainbarSetShimmer {
    position: absolute;
    inset: 0;
    opacity: 0.55;
}

// ── Shimmer base ──────────────────────────────────────────────────────────────
// Same technique as TakeStrip.shimmer.

.shimmer {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(
        105deg,
        var(--surface) 0%,
        var(--surface-hover) 40%,
        var(--surface) 55%,
        var(--surface-hover) 70%,
        var(--surface) 100%
    );
    background-size: 400% 100%;
    animation: shimmerSlide 2.2s ease-in-out infinite;
    pointer-events: none;
}

@keyframes shimmerSlide {
    from { background-position: 200% 0; }
    to   { background-position: -200% 0; }
}

// ── Card as pager (designer delta §5) ────────────────────────────────────────
// The prev zone takes the left 28% of the card because the dominant gesture is "forward" — the wide
// majority surface goes to it, the way a comic reader pages by tapping anywhere but the back edge.
// z-index 5 is load-bearing rather than arbitrary: the zones cover the whole card, so everything on
// it that takes a click (the look door, the coda) must sit above them.

.pager {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
}

.pagerZone {
    appearance: none;
    border: none;
    background: none;
    padding: 0 14px;
    margin: 0;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:focus-visible {
        outline: 2px solid var(--yolk);
        outline-offset: -3px;
        border-radius: 16px;
    }
}

.pagerZonePrev {
    flex: 0 0 28%;
    justify-content: flex-start;
}

.pagerZoneNext {
    flex: 1 1 auto;
    justify-content: flex-end;
}

// Feedback, not chrome: the discs exist only while a fine pointer is over the card (or a zone
// holds keyboard focus). display:none is the base state so a touch device never renders them at
// all — an always-on pair of arrows was rejected by the operator.
.pagerArrow {
    display: none;
    width: 34px;
    height: 34px;
    border-radius: var(--radius-full, 9999px);
    // The delta's own literal — a near-black disc darker than --surface, so it reads on a light
    // portrait as well as a dark one. No token carries this value.
    background: rgba(14, 14, 16, 0.72);
    color: var(--text);
    font-size: 16px;
    line-height: 1;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(6px);
}

@media (hover: hover) and (pointer: fine) {
    .pagerArrow {
        display: flex;
        opacity: 0;
        transition: opacity 160ms ease;
    }

    .bigArea:hover .pagerArrow,
    .pagerZone:focus-visible .pagerArrow {
        opacity: 1;
    }
}

// ── Intro dissolve overlay (Phase 2 Leg 5) ───────────────────────────────────
// A surface-colored overlay that dissolves out when the ad state mounts,
// revealing the showcase portrait. Gated by prefers-reduced-motion.

.introDissolve {
    position: absolute;
    inset: 0;
    z-index: 7;
    opacity: 0;
    pointer-events: none;
    background: var(--surface);
}

// Leg 5 — animation class applied in JSX. Plays once on ad mount, dissolves to opacity 0.
.introDissolveAnimate {
    animation: heroDissolveIn 900ms cubic-bezier(0.25, 1, 0.5, 1) forwards;
}

@keyframes heroDissolveIn {
    0%   { opacity: 1; }
    100% { opacity: 0; }
}

// ── Status chip (top-right) ───────────────────────────────────────────────────

.statusChip {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 9;
    font-family: var(--font-mono);
    font-size: 10.5px;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.88);
    background: rgba(0, 0, 0, 0.5);
    padding: 5px 10px;
    border-radius: 99px;
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    pointer-events: none;
}

// Pulsing dot (rendering / generating states)
.pulseDot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 99px;
    background: var(--yolk);
    animation: wbPulse 1s ease-in-out infinite;
    flex-shrink: 0;
}

// pulseDot on disabled CTA button (buying state) — dimmer
.ctaButtonDisabled .pulseDot {
    background: var(--text-dim);
}

@keyframes wbPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.55; transform: scale(0.75); }
}

// ── After label (bottom-right) ────────────────────────────────────────────────

.afterLabel {
    position: absolute;
    bottom: 14px;
    right: 14px;
    z-index: 6;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.82);
    background: rgba(0, 0, 0, 0.45);
    padding: 4px 9px;
    border-radius: 99px;
    backdrop-filter: blur(6px);
    pointer-events: none;
}

// ── Nine-thumb strip ──────────────────────────────────────────────────────────

.thumbStrip {
    display: flex;
    gap: 6px;
    align-items: stretch;
    list-style: none;
    padding: 0;
    margin: 0;
}

.thumb {
    flex: 1;
    position: relative;
    aspect-ratio: 3 / 4;
    border-radius: 7px;
    overflow: hidden;
    padding: 0;
    border: 1px solid var(--border);
    background: var(--surface-hover);
    cursor: default;
    display: block;
    min-width: 0;

    &:focus-visible {
        outline: 2px solid var(--yolk);
        outline-offset: 2px;
    }
}

.thumbActive {
    border: 2px solid var(--yolk);
}

.thumbDone {
    cursor: pointer;
    &:hover { border-color: var(--yolk-med); }
}

.thumbStreaming {
    border-color: var(--border-med);
}

.thumbLocked {
    cursor: default;
}

.thumbLockedOverlay {
    position: absolute;
    inset: 0;
    background: rgba(14, 14, 16, 0.55);
    filter: blur(8px) brightness(0.4) saturate(0.6);
    z-index: 1;
}

.thumbLockIcon {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.55);
}

.thumbImage {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 14%;
    display: block;
    z-index: 1;
}

.thumbImageDone {
    opacity: 1;
}

.thumbShimmer {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(
        105deg,
        var(--surface) 0%,
        var(--surface-hover) 40%,
        var(--surface) 55%,
        var(--surface-hover) 70%,
        var(--surface) 100%
    );
    background-size: 400% 100%;
    animation: shimmerSlide 2.2s ease-in-out infinite;
    pointer-events: none;
}

// ── CTA bar ───────────────────────────────────────────────────────────────────
// Renders below the strip/caption. Primary action for the stage.
// On desktop (≥1024) the left column carries the single primary CTA; hide the primary
// button + sub-note here so there is exactly one CTA on desktop. The ctaBar container
// stays rendered on all breakpoints — it holds the done-state regenerate ghost (.ctaGhost)
// and the always-on screen-reader live region (.srOnly).

.ctaBar {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 2px;
}

.ctaButton {
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: 500;
    border: none;
    border-radius: var(--radius-md, 10px);
    padding: 14px 20px;
    cursor: pointer;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 200ms ease, opacity 200ms ease;

    &:focus-visible {
        outline: 2px solid var(--yolk);
        outline-offset: 3px;
    }

    // On desktop (≥1024): left column carries the single primary CTA — hide the stage button.
    // Nested here so this @media emits AFTER the base display:flex in the compiled CSS.
    // A top-level @media placed before this class definition loses the cascade to the later base rule.
    @media (min-width: 1024px) {
        display: none;
    }
}

// The coda's press is part of the frame, not the bar: it shows at every width.
.codaButton {
    @media (min-width: 1024px) {
        display: flex;
    }
}

.ctaButtonActive {
    color: var(--ink);
    background: var(--yolk);
    &:hover { background: var(--yolk-hover); }
}

.ctaButtonDisabled {
    color: var(--text-dim);
    background: var(--surface-hover);
    cursor: default;
    opacity: 0.7;
}

.ctaButtonDone {
    color: var(--ink);
    background: var(--yolk);
    &:hover { background: var(--yolk-hover); }
}

.ctaNote {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
    text-align: center;
    margin: 0;
    letter-spacing: 0.04em;

    // On desktop (≥1024): hide alongside ctaButton (nested for correct cascade order).
    @media (min-width: 1024px) {
        display: none;
    }
}

// Ghost button (done state secondary action)
.ctaGhost {
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 400;
    color: var(--text-dim);
    background: none;
    border: 1px solid var(--border);
    border-radius: var(--radius-md, 10px);
    padding: 10px 20px;
    cursor: pointer;
    width: 100%;
    transition: color 150ms ease, border-color 150ms ease;

    &:hover {
        color: var(--text-muted);
        border-color: var(--border-med);
    }

    &:focus-visible {
        outline: 2px solid var(--yolk);
        outline-offset: 3px;
    }
}

// Leg 4 — visually-hidden live region (clip pattern, not display:none — SR must be able to observe it)
.srOnly {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

// ── Responsive (§08 complete, Leg 7) ─────────────────────────────────────────
// ≥1024px  = two-column; parent grid controls right-col width (~528px).
//            Big area fills flex:1 to ~660h within the grid row.
// 640–1023 = single column; stage is full-width. Big area min-height 380 → portrait orientation.
// <640     = mobile; stage height ~520h total (bigArea min-height 320 + strip + CTA).
//            Thumb strip wraps via gap tighten.
//            CTA full-width (already width:100% on ctaButton).

@media (max-width: 1023px) {
    // aspect-ratio:3/4 drives height from width on all viewports.
    // No min-height override needed — the ratio holds.

    // On tablet single-column the thumb strip can wrap if nImages × thumb > viewport.
    // flex-wrap allows it without overflow.
    .thumbStrip {
        flex-wrap: wrap;
    }
}

@media (max-width: 639px) {
    // §15 (canvas 06 revision): .bigArea's height now comes from .stage's max-height budget, not
    // an 80vh guess — see the base rules above.

    // §15 strip anatomy: <640 goes horizontal scroll-snap, never below the 44px touch floor (was:
    // flex-wrap + min-width:32px, which broke that floor — canvas's own "do not" list: "scale
    // thumbs below 44px to win room — take it from the image" is exactly what that did).
    .thumbStrip {
        flex-wrap: nowrap;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 2px;
    }

    .thumb {
        border-radius: 5px;
        flex: 0 0 44px;
        min-width: 44px;
        scroll-snap-align: start;
    }

    .ctaButton {
        font-size: 14px;
        padding: 12px 16px;
    }

    // Ghost button full-width on mobile (already 100% but restate for clarity)
    .ctaGhost {
        font-size: 12px;
        padding: 9px 16px;
    }
}

// ── Reduced motion (Leg 5 Phase 2 complete) ───────────────────────────────────
// All animations disabled. introDissolveAnimate shows final state (opacity:0) immediately.

@media (prefers-reduced-motion: reduce) {
    .shimmer,
    .thumbShimmer,
    .trainbarSetShimmer {
        animation: none;
    }

    .trainbarDot,
    .pulseDot {
        animation: none;
    }

    .trainbarBar i {
        transition: none;
    }

    // Leg 5: intro dissolve — skip animation, show final transparent state immediately.
    .introDissolveAnimate {
        animation: none;
        opacity: 0;
    }

    // The pager discs still appear on hover — they are the only signal that the card pages at all.
    // Only the fade is dropped.
    .pagerArrow {
        transition: none;
    }
}
